import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import * as z from 'zod';
import { publishBotExamViewModelSchema } from '@/shared/api/generated/schemas/publishBotExamViewModelSchema';

/** Função de tradução do namespace `examRevision`, usada para localizar as mensagens de validação. */
type PublishFormTranslator = TFunction<'examRevision'>;

/**
 * Cria o schema Zod do formulário de publicação, **derivado** do schema gerado
 * pelo Kubb (`publishBotExamViewModelSchema`) via `.pick`/`.extend` — nunca
 * recriado do zero. Recebe `t` para localizar as mensagens de validação.
 *
 * Regras de validação:
 * - `startDate`/`endDate` são obrigatórias e a de início deve ser anterior à de término.
 * - `weight` (peso) não pode ser negativo.
 * - `tries` (tentativas) deve ser no mínimo 1.
 * - `time` (tempo, em minutos) não pode ser negativo.
 * - `percentualComplexity` deve estar no intervalo 0–100.
 *
 * As datas são tratadas no formulário como strings `YYYY-MM-DD` (emitidas pelo
 * `DatePicker`) e convertidas para ISO date-time apenas na submissão.
 *
 * @param t - Função de tradução (`useTranslation('examRevision')`)
 * @returns Schema Zod do formulário de publicação
 */
export function createPublishFormSchema(t: PublishFormTranslator) {
  return publishBotExamViewModelSchema
    .pick({
      weight: true,
      randomize: true,
      percentualComplexity: true,
      tries: true,
      time: true,
      enableSafeEvaluationMode: true,
      teacherEvaluationExamId: true,
    })
    .extend({
      startDate: z.string().min(1, t('publish.form.errors.startRequired')),
      endDate: z.string().min(1, t('publish.form.errors.endRequired')),
      weight: z.number().int().min(0, t('publish.form.errors.weightNegative')),
      randomize: z.boolean(),
      percentualComplexity: z
        .number()
        .int()
        .min(0, t('publish.form.errors.complexityRange'))
        .max(100, t('publish.form.errors.complexityRange')),
      tries: z.number().int().min(1, t('publish.form.errors.triesMin')),
      time: z.number().int().min(0, t('publish.form.errors.timeNegative')),
      enableSafeEvaluationMode: z.boolean(),
      teacherEvaluationExamId: z.number().int().nullable(),
    })
    .refine(
      values =>
        !values.startDate || !values.endDate
          ? true
          : dayjs(values.startDate).isBefore(dayjs(values.endDate)),
      { path: ['endDate'], message: t('publish.form.errors.dateOrder') },
    );
}

/** Valores do formulário de publicação. */
export type PublishFormValues = z.infer<ReturnType<typeof createPublishFormSchema>>;
